import { useState, useEffect } from 'react'
import {
  Grid,
  Button,
  Header,
  Loader,
  Input,
  Icon,
  Divider,
  Modal,
  Dropdown,
} from 'semantic-ui-react'
import { toast } from 'react-toastify'
import './ExchangeTable.css'
import env from '../../../../../env'
import { CaretDown } from 'phosphor-react'
import { useSelector } from 'react-redux'
import { useWebsocket } from '../../../../../hooks/useWebsocket'
import { track } from '@amplitude/analytics-browser'
import moment from 'moment-timezone'
import { us_timezones } from '../../../../../helpers/helpers'
import { hasPricing } from '../../../../../lib/utils/utils'

const EditOrderModal = (props) => {
  const {
    setOpenEditBidAskModal,
    openEditBidAskModal,
    editingOrder,
    editingExchangeOrderBook,
    setEditingOrder,
    setEditingExchangeOrderBook,
    globalSettings,
    user,
    business,
    setReloadExchange,
    setExchangeTableLoaded,
    setActionBeforeWsReset,
    setInputExpiration,
    inputExpiration,
    is_admin,
    currentPortal,
    inputPrice,
    productVariant,
  } = props
  const [editedQuantity, setEditedQuantity] = useState(null)
  const [editedPrice, setEditedPrice] = useState(null)
  const [editedSecondaryPrice, setEditedSecondaryPrice] = useState(null)
  const { ws, resetWs, editOrderWsMessage } = useWebsocket()

  const [loadingEditBid, setLoadingEditBid] = useState(false)
  const tenant = useSelector((state) => state.globals.tenant)

  function handleEditBidAskModal() {
    setLoadingEditBid(true)
    if (ws && ws.readyState === 1) {
      let orderId = editingOrder.id
      let expires_at = inputExpiration
      if (inputExpiration === 'End of Day') {
        expires_at = moment()
          .tz(us_timezones[user?.current_location?.state])
          .endOf('day')
          .toISOString()
      }
      if (inputExpiration === 'Good Till Cancelled') {
        expires_at = null
      }
      const update_order = {
        order_id: orderId,
        quantity: editedQuantity,
        status: 'open',
      }
      // With primary pricing enabled:
      // - Admins: send both price and secondary_price
      // - Suppliers: send secondary_price only
      // - Buyers: send price only
      // Without primary pricing: everyone sends price only
      if (hasPricing(user, 'primary')) {
        if (is_admin) {
          // Admin sends both
          update_order.price = editedPrice
          update_order.secondary_price = editedSecondaryPrice
        } else if (currentPortal === 'supplier') {
          // Supplier sends secondary_price only
          update_order.secondary_price = editedSecondaryPrice
        } else {
          // Buyer sends price only
          update_order.price = editedPrice
        }
      } else {
        // No primary pricing - everyone sends price
        update_order.price = editedPrice
      }
      let message = {
        tenant_id: tenant.id,
        update_order: [update_order],
      }
      if (expires_at) {
        message['update_order'][0]['expires_at'] = expires_at
      }
      track('Edit Bid', {
        'User ID': user?.id,
        'Business Name': business?.name,
      })
      // Validate the price field(s) being submitted
      const priceToValidate = hasPricing(user, 'primary') && currentPortal === 'supplier' && !is_admin
        ? editedSecondaryPrice
        : editedPrice
      if (priceToValidate < 0) {
        toast.error('Please enter valid price')
      } else if (editedQuantity < 0) {
        toast.error('Please enter valid quantity')
      } else {
        ws.send(editOrderWsMessage(message))
        setLoadingEditBid(false)
        setExchangeTableLoaded(false)
        toast.success('Updated bid successfully!')
        setOpenEditBidAskModal(false)
        setEditedQuantity(null)
        setEditedPrice(null)
        setEditingOrder()
        setEditingExchangeOrderBook()
      }
      if (is_admin) {
        setReloadExchange(true)
      }
    } else {
      resetWs()
      setActionBeforeWsReset(handleEditBidAskModal)
    }
    setLoadingEditBid(false)
  }

  useEffect(() => {
    if (editingOrder) {
      setEditedPrice(editingOrder.price)
      setEditedSecondaryPrice(editingOrder.secondary_price)
      setEditedQuantity(editingOrder.quantity)
    }
  }, [editingOrder])

  return (
    <>
      <Modal
        onOpen={() => setOpenEditBidAskModal(true)}
        onClose={() => setOpenEditBidAskModal(false)}
        open={openEditBidAskModal}
        style={{
          minHeight: '500px',
          padding: '2rem',
        }}
        size="mini"
        closeOnDimmerClick={false}
        closeIcon={true}
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: 'inherit',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: '0 1 20%',
            }}
          >
            <Header
              as={'h1'}
              style={{ marginBottom: '.5rem' }}
              textAlign="center"
            >
              Edit {currentPortal === 'supplier' ? 'Sell' : 'Buy'}
            </Header>
            <Header.Subheader
              as={'h4'}
              style={{
                textAlign: 'center',
                marginTop: '0',
                marginBottom: '.5rem',
              }}
            >
              {editingExchangeOrderBook &&
                editingExchangeOrderBook?.product_variant.product.material.name}
              {', '}
              {editingExchangeOrderBook?.product_variant &&
                editingExchangeOrderBook.product_variant.attribute_selections.map(
                  (attribute, index) => {
                    let check =
                      index !==
                      editingExchangeOrderBook.product_variant
                        .attribute_selections.length -
                        1
                    return (
                      attribute.attribute_selection_value + (check ? ', ' : '')
                    )
                  }
                )}
            </Header.Subheader>
          </div>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: '0 1 65%',
              width: '100%',
              // overflow: "scroll",
              justifyContent: 'center',
            }}
          >
            <Grid style={{ padding: '2rem 0 0 0' }}>
              <Grid.Column>
                {/* Primary pricing enabled (user has PRIMARY pricing group) */}
                {hasPricing(user, 'primary') ? (
                  <>
                    {/* Admins with primary pricing see both Primary and Secondary prices */}
                    {is_admin && (
                      <>
                        <Header as={'h4'} textAlign="center">
                          Primary Price
                        </Header>
                        <div style={{ position: 'relative' }}>
                          <Input
                            icon={
                              <Icon
                                name="dollar"
                                size="big"
                                style={{ width: '20px' }}
                              />
                            }
                            defaultValue={inputPrice}
                            value={editedPrice}
                            iconPosition={'left'}
                            className="price-modal-input"
                            type={'number'}
                            onChange={(e, d) => {
                              if (d.value < 0) {
                                toast.error('Price must be greater than 0')
                                setEditedPrice(null)
                              } else if (
                                globalSettings?.round_exchange_order_prices &&
                                d.value % 1 !== 0
                              ) {
                                toast.error('Price must be a whole number')
                                setEditedPrice(null)
                              } else if (d.value > 999999) {
                                toast.error('Price must be less than 1000000')
                                setEditedPrice(null)
                              } else {
                                setEditedPrice(d.value)
                              }
                            }}
                            step={'1'}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              color: 'grey',
                              right: '0',
                              top: '0',
                              bottom: '0',
                              display: 'flex',
                              alignItems: 'center',
                              paddingRight: '1rem',
                            }}
                          >
                            /
                            {productVariant?.unit_name
                              ? productVariant?.unit_name
                              : 'MBF'}
                          </div>
                        </div>
                        <Divider />
                        <Header as={'h4'} textAlign="center">
                          Secondary Price
                        </Header>
                        <div style={{ position: 'relative' }}>
                          <Input
                            icon={
                              <Icon
                                name="dollar"
                                size="big"
                                style={{ width: '20px' }}
                              />
                            }
                            value={editedSecondaryPrice}
                            iconPosition={'left'}
                            className="price-modal-input"
                            type={'number'}
                            onChange={(e, d) => {
                              if (d.value < 0) {
                                toast.error('Secondary price must be greater than 0')
                                setEditedSecondaryPrice(null)
                              } else if (
                                globalSettings?.round_exchange_order_prices &&
                                d.value % 1 !== 0
                              ) {
                                toast.error('Secondary price must be a whole number')
                                setEditedSecondaryPrice(null)
                              } else if (d.value > 999999) {
                                toast.error(
                                  'Secondary price must be less than 1000000'
                                )
                                setEditedSecondaryPrice(null)
                              } else {
                                setEditedSecondaryPrice(d.value)
                              }
                            }}
                            step={'1'}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              color: 'grey',
                              right: '0',
                              top: '0',
                              bottom: '0',
                              display: 'flex',
                              alignItems: 'center',
                              paddingRight: '1rem',
                            }}
                          >
                            /
                            {productVariant?.unit_name
                              ? productVariant?.unit_name
                              : 'MBF'}
                          </div>
                        </div>
                      </>
                    )}
                    {/* Buyers with primary pricing (non-admin) see only Primary price */}
                    {!is_admin && props.currentPortal === 'buyer' && (
                      <>
                        <Header as={'h4'} textAlign="center">
                          Your Price
                        </Header>
                        <div style={{ position: 'relative' }}>
                          <Input
                            icon={
                              <Icon
                                name="dollar"
                                size="big"
                                style={{ width: '20px' }}
                              />
                            }
                            defaultValue={inputPrice}
                            value={editedPrice}
                            iconPosition={'left'}
                            className="price-modal-input"
                            type={'number'}
                            onChange={(e, d) => {
                              if (d.value < 0) {
                                toast.error('Price must be greater than 0')
                                setEditedPrice(null)
                              } else if (
                                globalSettings?.round_exchange_order_prices &&
                                d.value % 1 !== 0
                              ) {
                                toast.error('Price must be a whole number')
                                setEditedPrice(null)
                              } else if (d.value > 999999) {
                                toast.error('Price must be less than 1000000')
                                setEditedPrice(null)
                              } else {
                                setEditedPrice(d.value)
                              }
                            }}
                            step={'1'}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              color: 'grey',
                              right: '0',
                              top: '0',
                              bottom: '0',
                              display: 'flex',
                              alignItems: 'center',
                              paddingRight: '1rem',
                            }}
                          >
                            /
                            {productVariant?.unit_name
                              ? productVariant?.unit_name
                              : 'MBF'}
                          </div>
                        </div>
                      </>
                    )}
                    {/* Suppliers with primary pricing (non-admin) see only Secondary price */}
                    {!is_admin && props.currentPortal === 'supplier' && (
                      <>
                        <Header as={'h4'} textAlign="center">
                          Your Price
                        </Header>
                        <div style={{ position: 'relative' }}>
                          <Input
                            icon={
                              <Icon
                                name="dollar"
                                size="big"
                                style={{ width: '20px' }}
                              />
                            }
                            value={editedSecondaryPrice}
                            iconPosition={'left'}
                            className="price-modal-input"
                            type={'number'}
                            onChange={(e, d) => {
                              if (d.value < 0) {
                                toast.error('Price must be greater than 0')
                                setEditedSecondaryPrice(null)
                              } else if (
                                globalSettings?.round_exchange_order_prices &&
                                d.value % 1 !== 0
                              ) {
                                toast.error('Price must be a whole number')
                                setEditedSecondaryPrice(null)
                              } else if (d.value > 999999) {
                                toast.error(
                                  'Price must be less than 1000000'
                                )
                                setEditedSecondaryPrice(null)
                              } else {
                                setEditedSecondaryPrice(d.value)
                              }
                            }}
                            step={'1'}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              color: 'grey',
                              right: '0',
                              top: '0',
                              bottom: '0',
                              display: 'flex',
                              alignItems: 'center',
                              paddingRight: '1rem',
                            }}
                          >
                            /
                            {productVariant?.unit_name
                              ? productVariant?.unit_name
                              : 'MBF'}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* No primary pricing - simple single price field for everyone */
                  <>
                    <Header as={'h4'} textAlign="center">
                      Your Price
                    </Header>
                    <div style={{ position: 'relative' }}>
                      <Input
                        icon={
                          <Icon
                            name="dollar"
                            size="big"
                            style={{ width: '20px' }}
                          />
                        }
                        defaultValue={inputPrice}
                        value={editedPrice}
                        iconPosition={'left'}
                        className="price-modal-input"
                        type={'number'}
                        onChange={(e, d) => {
                          if (d.value < 0) {
                            toast.error('Price must be greater than 0')
                            setEditedPrice(null)
                          } else if (
                            globalSettings?.round_exchange_order_prices &&
                            d.value % 1 !== 0
                          ) {
                            toast.error('Price must be a whole number')
                            setEditedPrice(null)
                          } else if (d.value > 999999) {
                            toast.error('Price must be less than 1000000')
                            setEditedPrice(null)
                          } else {
                            setEditedPrice(d.value)
                          }
                        }}
                        step={'1'}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          color: 'grey',
                          right: '0',
                          top: '0',
                          bottom: '0',
                          display: 'flex',
                          alignItems: 'center',
                          paddingRight: '1rem',
                        }}
                      >
                        /
                        {productVariant?.unit_name
                          ? productVariant?.unit_name
                          : 'MBF'}
                      </div>
                    </div>
                  </>
                )}

                <Divider />
                <Header as={'h4'} textAlign="center">
                  Quantity
                </Header>
                <div style={{ position: 'relative' }}>
                  <Input
                    icon={
                      <Icon name="truck" size="big" style={{ width: '20px' }} />
                    }
                    iconPosition={'left'}
                    value={editedQuantity}
                    className="price-modal-input"
                    type={'number'}
                    onChange={(e, d) => {
                      if (d.value < 0) {
                        toast.error('Quantity must be greater than 0')
                        setEditedQuantity(null)
                      } else if (d.value % 1 !== 0) {
                        toast.error('Quantity must be a whole number')
                        setEditedQuantity(null)
                      } else if (d.value > 999999) {
                        toast.error('Quantity must be less than 1000000')
                        setEditedQuantity(null)
                      } else {
                        setEditedQuantity(d.value)
                      }
                    }}
                    step={1}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      color: 'grey',
                      right: '0',
                      top: '0',
                      bottom: '0',
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: '1rem',
                    }}
                  >
                    Trucks
                  </div>
                </div>
                <Divider />
                <Header as={'h4'} textAlign="center">
                  Expiration
                </Header>
                <div style={{ position: 'relative', overflow: 'visible' }}>
                  <Dropdown
                    id="expiration-dropdown"
                    icon={<CaretDown style={{ float: 'right' }} />}
                    placeholder="Select Expiration"
                    fluid
                    selection
                    defaultValue={inputExpiration}
                    style={{
                      marginBottom: '1rem',
                      maxWidth: '100%',
                      zIndex: '1001',
                    }}
                    options={[
                      {
                        text: 'End of Day',
                        value: 'End of Day',
                      },
                      {
                        text: 'Good Till Cancelled',
                        value: 'Good Till Cancelled',
                      },
                    ]}
                    onChange={(e, d) => setInputExpiration(d.value)}
                  />
                </div>
                <Divider />
              </Grid.Column>
            </Grid>
          </div>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: '0 1 15%',
              width: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              style={{
                '--primaryColor': tenant?.deployment_primary_color,
                display: 'flex',
                justifyContent: 'center',
              }}
              loading={loadingEditBid}
              disabled={
                // With primary pricing: admins need both, buyers need primary, suppliers need secondary
                // Without primary pricing: everyone needs the single price field
                (hasPricing(user, 'primary')
                  ? is_admin
                    ? !editedPrice || !editedSecondaryPrice
                    : props.currentPortal === 'supplier'
                      ? !editedSecondaryPrice
                      : !editedPrice
                  : !editedPrice) ||
                !editedQuantity ||
                !inputExpiration
              }
              color="primary"
              onClick={() => handleEditBidAskModal()}
              clearable
            >
              {loadingEditBid && <Loader size="tiny" inline="centered" />}
              {!loadingEditBid && 'Update'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
export default EditOrderModal
